import type { DiscoveryOperation } from "@/services/operationsService";

/**
 * Extract the short connector name from a connector ID path.
 * - `".../managedApis/office365"` → `"office365"`
 * - `"/serviceProviders/AzureBlob"` → `"AzureBlob"`
 */
export function extractConnectorName(connectorId: string): string {
  return connectorId.split('/').pop() ?? '';
}

/**
 * Derive a human-readable fallback display name from a connector API ID.
 * - `/managedApis/msnweather` → `"Msnweather"` (capitalised)
 * - `/serviceProviders/AzureBlob` → `"AzureBlob"` (last segment as-is)
 *
 * Used when connector metadata is unavailable (fetch failed / not fetched);
 * canonical display names come from metadata or the built-in operation set.
 */
export function deriveConnectorDisplayName(apiId: string): string {
  const match = apiId.match(/managedApis\/([^/]+)$/i);
  if (match?.[1]) {
    const name = match[1];
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  // Fallback: return last segment of path
  const segments = apiId.split('/');
  return segments[segments.length - 1] || apiId;
}

/**
 * Normalize an API ID to a comparable key that preserves the connector type.
 * This ensures `/managedApis/sql` and `/serviceProviders/sql` are distinct.
 *
 * - `/managedApis/sql` → `managedapis/sql`
 * - `/serviceProviders/AzureBlob` → `serviceproviders/azureblob`
 * - `sql` (bare name) → `sql` (can match either type)
 */
export function normalizeApiId(apiId: string): string {
  if (!apiId) return '';
  const lower = apiId.toLowerCase();
  
  // Extract type prefix and connector name for full path IDs
  const managedMatch = lower.match(/managedapis\/([^/]+)$/);
  if (managedMatch) return `managedapis/${managedMatch[1]}`;
  
  const serviceProviderMatch = lower.match(/serviceproviders\/([^/]+)$/);
  if (serviceProviderMatch) return `serviceproviders/${serviceProviderMatch[1]}`;
  
  const connectionProviderMatch = lower.match(/connectionproviders\/([^/]+)$/);
  if (connectionProviderMatch) return `connectionproviders/${connectionProviderMatch[1]}`;
  
  const agentMatch = lower.match(/agentconnections\/([^/]+)$/);
  if (agentMatch) return `agentconnections/${agentMatch[1]}`;
  
  // Return the last segment for bare names (backwards compatibility)
  return extractConnectorName(apiId).toLowerCase();
}

/**
 * Extract the last non-empty path segment, lowercased.
 * e.g. `"/automationProjects/proj/managedApis/office365"` → `"office365"`
 *
 * TODO: Remove once connector IDs are normalised consistently upstream.
 */
export function getLastSegment(path: string): string {
  return path.split('/').filter(Boolean).pop()?.toLowerCase() ?? path.toLowerCase();
}

/**
 * Check whether a candidate connector ID matches a filter ID.
 *
 * - For multi-segment filters (containing `/`, e.g. `"connectionProviders/control"`)
 *   a case-insensitive `endsWith` comparison is used.
 * - For simple name filters (e.g. `"office365"`) only the last path segment of the
 *   candidate is compared (exact, case-insensitive).
 *
 * TODO: Remove once connector IDs are normalised consistently upstream.
 */
export function matchesConnectorFilter(candidate: string, filterId: string): boolean {
  if (filterId.includes('/')) {
    return candidate.toLowerCase().endsWith(filterId.toLowerCase());
  }
  return getLastSegment(candidate) === filterId.toLowerCase();
}

/**
 * Check whether a connector ID refers to a client / in-app built-in
 * connector. Matches the `connectionProviders/{name}` slug convention
 * used by curated client operations (HTTP, Control, Variables, Compose…)
 * and runtime built-ins that follow the same id pattern. Accepts both
 * the bare form (`connectionProviders/http`) and the slash-prefixed
 * form (`/connectionProviders/http`) for safety. Case-insensitive, like
 * its sibling `is*Connector` helpers.
 */
export function isClientBuiltInConnector(connectorId: string | null | undefined): boolean {
  if (!connectorId) return false;
  const lower = connectorId.toLowerCase();
  return lower.startsWith('connectionproviders/') || lower.includes('/connectionproviders/');
}

/**
 * Check whether a connector ID refers to a service provider connector.
 * Matches path-based (`/serviceProviders/AzureBlob`) format. Case-insensitive
 * to match the behavior of `isManagedApiConnector` and `isAgentConnectionId`.
 */
export function isServiceProviderConnector(connectorId: string): boolean {
  return connectorId.toLowerCase().includes('/serviceproviders/');
}

/**
 * Extract the service provider name (e.g. `"AzureBlob"`) from a service-provider
 * connector ID. Case-insensitive — accepts both `/serviceProviders/...` and
 * `/serviceproviders/...` (the lowercased form produced by `normalizeConnectorId`).
 *
 * Returns the original `connectorId` unchanged if no `/serviceProviders/{name}`
 * segment is present, so callers can detect non-matches.
 */
export function extractServiceProviderName(connectorId: string): string {
  const match = connectorId.match(/\/serviceProviders\/([^/]+)/i);
  return match?.[1] ?? connectorId;
}

/**
 * Extract the operation group name (lowercased) from a connector ID.
 * Used to address runtime endpoints under `/operationGroups/{group}`.
 * - `/serviceProviders/azureTables` → `azuretables`
 * - `connectionProviders/xmlOperations` → `xmloperations`
 * - `/subscriptions/.../managedApis/teams` → `teams`
 * - bare `office365` → `office365`
 */
export function extractOperationGroup(connectorId: string): string {
  const lower = connectorId.toLowerCase();
  const match =
    lower.match(/\/serviceproviders\/([^/]+)/) ??
    lower.match(/\/?connectionproviders\/([^/]+)/) ??
    lower.match(/\/managedapis\/([^/]+)/);
  if (match?.[1]) return match[1];
  const segments = connectorId.split('/').filter(Boolean);
  return segments[segments.length - 1]?.toLowerCase() ?? lower;
}

/**
 * Path/substring patterns that unambiguously identify an agent-style connection.
 *
 * Agent connections show up under several aliases depending on the source:
 *   - `/agentConnections/{name}` — emitted by `extractConnectionReferences` when reading connections.json.
 *   - `connectionProviders/agent` / `/connectionProviders/agent` — used by agent operation manifests
 *     (agentloop, foundryAgent, handoff) and by `connections.json` `modelProvider.id`.
 *   - `/providers/Microsoft.Web/connections/agent...` — ARM resource ids for agent connections.
 *   - `/providers/Microsoft.CognitiveServices/...` — ARM resource ids for AzureOpenAI-backed agent
 *     connections (the `connectorId` returned by the data-plane list API can point at the cognitive
 *     services account).
 *
 * Patterns are matched case-insensitively. Note: each pattern is intentionally rooted at a path
 * segment (`/segment/`) to avoid false positives like `/apis/openai`, which is a managed connector,
 * not an agent connection. Bare provider identifiers (`OpenAI`, `AzureOpenAI`) are matched
 * separately as exact / last-segment strings — see {@link AGENT_PROVIDER_IDS}.
 */
const AGENT_CONNECTION_ID_PATH_PATTERNS = [
  '/agentconnections/',
  '/hostedagentconnections/',
  'connectionproviders/agent',
  '/providers/microsoft.web/connections/agent',
  '/providers/microsoft.cognitiveservices/',
] as const;

/**
 * Bare provider identifiers used as the `api.id` for agent references when there is no
 * full path (e.g. `connections.json` `modelProvider.id`). Matched as exact strings or as
 * the final path segment, case-insensitively.
 *
 * `microsoftmanaged` covers the Microsoft-managed synthetic reference.
 * It needs to classify as an agent connection so the
 * ConnectionTab and picker filters treat it consistently with BYOM agent refs.
 * The `MANAGED_CONNECTOR_PATH_PATTERNS` short-circuit above keeps
 * `/managedApis/microsoftmanaged`-style ids from being mis-classified — Microsoft-managed
 * refs never use a managed-API path.
 */
const AGENT_PROVIDER_IDS = ['openai', 'azureopenai', 'microsoftmanaged'] as const;

/** Path prefixes used by managed connectors — short-circuited so they are never classified as agent. */
const MANAGED_CONNECTOR_PATH_PATTERNS = ['/managedapis/', '/apis/'] as const;

/**
 * Check whether a connector / api id refers to an agent connection
 * (Azure OpenAI, OpenAI, Foundry Agent, etc.).
 *
 * Use this for *classification* of an existing connection / reference. For the
 * stricter "is this connector created locally vs. in ARM" check used during the
 * connection creation flow, see [`isAgentConnector`](../services/connectionCreationService.ts).
 */
export function isAgentConnectionId(connectorId: string | null | undefined): boolean {
  if (!connectorId) return false;
  const lower = connectorId.toLowerCase();

  // Managed connector ids (e.g. `/apis/openai`, `/managedApis/azureopenai`) are never agent
  // connections, even though their last segment may collide with a provider id.
  if (MANAGED_CONNECTOR_PATH_PATTERNS.some((p) => lower.includes(p))) {
    return false;
  }

  if (AGENT_CONNECTION_ID_PATH_PATTERNS.some((p) => lower.includes(p))) {
    return true;
  }

  // Bare provider id forms — exact match, or the final path segment of an otherwise unknown id.
  // Still gated on not being a managed connector (handled above).
  if (AGENT_PROVIDER_IDS.includes(lower as (typeof AGENT_PROVIDER_IDS)[number])) {
    return true;
  }
  const lastSegment = lower.split('/').filter(Boolean).pop();
  return lastSegment !== undefined && AGENT_PROVIDER_IDS.includes(lastSegment as (typeof AGENT_PROVIDER_IDS)[number]);
}

/**
 * Check whether a connector / api id refers specifically to a **hosted-agent**
 * (FoundryHostedAgent) connection — i.e. an entry from the `hostedAgentConnections`
 * section, which `extractConnectionReferences` maps to `/hostedAgentConnections/{key}`.
 *
 * Hosted-agent connections are a distinct type from the `agentConnections` used by
 * native / foundry / managed agents, and are only valid in the Foundry Hosted Agent
 * context (rendered via `HostedAgentConnectionTab`). Note that {@link isAgentConnectionId}
 * also returns `true` for these ids (they share the agent path patterns), so use this
 * narrower check when you specifically need to distinguish hosted-agent connections.
 */
export function isHostedAgentConnectionId(connectorId: string | null | undefined): boolean {
  return !!connectorId && connectorId.toLowerCase().includes('/hostedagentconnections/');
}

/**
 * Case-insensitive check of whether the connection reference bound to `key` is a
 * hosted-agent connection. Reference keys drift in casing between workflow.json and the
 * store (see the case-insensitive fallback in <c>useNodeConnectionStatus</c>), so match
 * keys case-insensitively before classifying the api id — otherwise a hosted-agent binding
 * can be missed when only casing differs.
 */
export function isHostedAgentConnectionReference(
  references: Record<string, { api?: { id?: string | null } | null } | undefined> | null | undefined,
  key: string | null | undefined,
): boolean {
  if (!references || !key) return false;
  const direct = references[key];
  if (direct) return isHostedAgentConnectionId(direct.api?.id);
  const lower = key.toLowerCase();
  for (const refKey of Object.keys(references)) {
    if (refKey.toLowerCase() === lower) {
      return isHostedAgentConnectionId(references[refKey]?.api?.id);
    }
  }
  return false;
}

/**
 * Normalize connector ID for consistent cache keys
 * Following LogicAppsUX pattern of using lowercase IDs
 */
export const normalizeConnectorId = (connectorId: string | null): string | null => {
  if (!connectorId) return null;

  // Normalize managed API IDs to canonical /managedapis/{name} format
  if (isManagedApiConnector(connectorId)) {
    return toShortManagedApiId(connectorId).toLowerCase();
  }

  return connectorId.toLowerCase();
};

export const isManagedApiOperation = (operation: DiscoveryOperation): boolean => {
  // Azure managed API operations have type "automationProjects/managedApis/apiOperations"
  // but should be created as "ApiConnection" type in the workflow
  return operation.type?.toLowerCase() === 'automationprojects/managedapis/apioperations';
};

export const isManagedApiConnector = (connectorId: string | null): boolean => {
  return connectorId?.toLowerCase().includes('/managedapis/') ?? false;
};

/**
 * Normalize any managed API connector ID to the canonical `/managedApis/{name}` format.
 * Handles:
 *   - ARM: `/subscriptions/.../providers/Microsoft.Web/locations/.../managedApis/office365`
 *   - Short ARM: `/locations/.../managedApis/office365`
 *   - Data-plane: `/automationProjects/.../managedApis/office365`
 *   - Already canonical: `/managedApis/office365`
 *
 * Returns the original string unchanged if it is not a managed API connector.
 */
export const toShortManagedApiId = (connectorId: string): string => {
  // Any path ending in /managedApis/{name} — extract just the tail
  const pathMatch = connectorId.match(/\/managedApis\/([^/]+)$/i);
  if (pathMatch?.[1]) {
    return `/managedApis/${pathMatch[1]}`;
  }
  return connectorId;
};
